/**
 * La campanita del header: lo que no cierra con la plata, el stock o los
 * clientes. Lo que solo deja el catalogo incompleto (sin foto, sin costo) no
 * entra; ver `rutas/avisos.ts` en el backend.
 *
 * El numero es la suma de casos, no de avisos: "3 cheques vencidos" pesa 3,
 * porque son 3 cosas que alguien tiene que resolver. Se pinta rojo si
 * hay algun ERROR (un dato que no cierra) y ambar si solo hay avisos o faltas.
 *
 * Cada aviso se despliega y lista sus casos con link a donde se arreglan. Al
 * seguir un link el panel se cierra, y el contador baja solo al refrescar.
 */
import { Link } from "@tanstack/react-router";
import { Bell, ChevronRight } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAvisos, type Aviso } from "@/lib/queries";
import { cn } from "@/lib/utils";

const COLOR: Record<Aviso["tipo"], string> = {
  ERROR: "text-destructive",
  AVISO: "text-warning",
  FALTA: "text-muted-foreground",
};

function FilaAviso({ aviso, alSeguir }: { aviso: Aviso; alSeguir: () => void }): JSX.Element {
  const [abierto, setAbierto] = useState(false);

  return (
    <li className="border-b border-border last:border-b-0">
      <Button
        variant="ghost"
        aria-expanded={abierto}
        onClick={() => setAbierto((v) => !v)}
        className="h-auto w-full items-start justify-start whitespace-normal px-1 py-2 text-left font-normal sm:h-auto"
      >
        <ChevronRight
          aria-hidden="true"
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform duration-150",
            abierto && "rotate-90",
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{aviso.titulo}</span>
          <span className="block text-xs text-muted-foreground">{aviso.detalle}</span>
        </span>
        <span className={cn("text-sm font-semibold tabular-nums", COLOR[aviso.tipo])}>
          {aviso.cantidad}
        </span>
      </Button>

      {abierto && (
        <ul className="mb-2 ml-6 space-y-0.5">
          {aviso.casos.map((caso, i) => (
            <li key={i} className="truncate text-xs">
              {caso.ruta ? (
                <Link
                  to={caso.ruta as never}
                  onClick={alSeguir}
                  className="text-foreground underline-offset-2 hover:underline"
                >
                  {caso.texto}
                </Link>
              ) : (
                <span className="text-muted-foreground">{caso.texto}</span>
              )}
            </li>
          ))}
          {aviso.cantidad > aviso.casos.length && (
            <li className="text-xs text-muted-foreground">
              y {aviso.cantidad - aviso.casos.length} más
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

export function Avisos(): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const { data, refetch } = useAvisos();

  const avisos = data?.avisos ?? [];
  const total = data?.total ?? 0;
  const hayError = avisos.some((a) => a.tipo === "ERROR");

  return (
    <Popover
      open={abierto}
      onOpenChange={(v) => {
        setAbierto(v);
        if (v) void refetch();
      }}
    >
      {/* El numero va al lado del boton y no adentro: `IconButton` no lleva hijos. */}
      <span className="relative inline-flex shrink-0">
        <PopoverTrigger asChild>
          <IconButton
            icono={Bell}
            variant="outline"
            aria-label={total > 0 ? `${total} avisos de datos` : "Sin avisos de datos"}
            className={cn(
              total === 0 && "text-muted-foreground hover:text-foreground",
              total > 0 &&
                (hayError ? "border-destructive text-destructive" : "border-warning text-warning"),
            )}
          />
        </PopoverTrigger>
        {total > 0 && (
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute -right-1.5 -top-1.5 min-w-5 rounded-full px-1 text-center text-xs font-semibold tabular-nums leading-5",
              hayError
                ? "bg-destructive text-destructive-foreground"
                : "bg-warning text-warning-foreground",
            )}
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </span>
      <PopoverContent align="end" className="w-80 p-3 sm:w-96">
        <p className="mb-1 text-sm font-semibold">Datos para revisar</p>
        {avisos.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            Todo en orden: nada que no cierre con la plata o el stock.
          </p>
        ) : (
          <ul className="max-h-96 overflow-y-auto">
            {avisos.map((a) => (
              <FilaAviso key={a.clave} aviso={a} alSeguir={() => setAbierto(false)} />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
